import * as React from 'react';
import { Box, Collapse, IconButton, Stack, TableRow as MUITableRow, TableRowProps } from '@mui/material';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TableCell from '@mui/material/TableCell';

interface TableRowPropsTypes<T> {
  data: T;
  isSelected?: boolean;
  storagePrefix?: string;
  tableRowProps?: TableRowProps;
  children?: React.ReactNode;
  drawerColumnSpan?: number;
  drawerButtonBar?: React.ReactNode;
  drawerChildren?: React.ReactNode;
  drawerOpened?(opened: boolean, data: T): void;
  rowClicked?(data: T, event: React.MouseEvent): void;
  rowDoubleClicked?(data: T, event: React.MouseEvent): void;
}

function TableRow<T>(props: TableRowPropsTypes<T>) {
  const storageKey = `${props.storagePrefix || 'drawer'}_${props.data}`;

  const [drawerOpened, setDrawerOpen] = React.useState(() => {
    const saved = sessionStorage.getItem(storageKey);
    return saved === 'true';
  });

  const doubleClickTimer = React.useRef<NodeJS.Timeout | null>(null);

  // Call drawerOpened callback on initial mount if drawer was restored as open
  React.useEffect(() => {
    if (drawerOpened && props.drawerOpened) {
      props.drawerOpened(true, props.data);
    }
  }, []); // Empty dependency array - only run on mount

  const updateDrawerState = (newState: boolean) => {
    setDrawerOpen(newState);
    sessionStorage.setItem(storageKey, String(newState));
  };

  const handleRowClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (doubleClickTimer.current) {
      clearTimeout(doubleClickTimer.current);
      doubleClickTimer.current = null;
      if (props.rowDoubleClicked) {
        props.rowDoubleClicked(props.data, e);
      }
    } else {
      doubleClickTimer.current = setTimeout(() => {
        doubleClickTimer.current = null;

        if (props.drawerChildren)
          updateDrawerState(!drawerOpened);

        if (props.drawerOpened) {
          props.drawerOpened(!drawerOpened, props.data);
        }

        if (props.rowClicked) {
          props.rowClicked(props.data, e);
        }
      }, 300);
    }
  };


  return (
    <>
      <MUITableRow
        {...props.tableRowProps}
        hover
        onClick={handleRowClick}
        role="checkbox"
        tabIndex={-1}
        selected={props.isSelected}
        sx={{
          ...props.tableRowProps?.sx,
          ...(props.isSelected ? { bgcolor: 'rgba(25, 118, 210, 0.08)' } : {}),
          ...(props.drawerChildren ? {
            cursor: 'pointer',
            '&.Mui-selected:hover': {
              bgcolor: 'rgba(25, 118, 210, 0.12)',
            },
            height: '44px', // Reduce the row height
          } : {}),
          '& > td, & > th': {
            padding: '6px 16px', // Reduce vertical padding
            borderBottom: '1px solid rgba(224, 224, 224, 0.5)', // Lighter border
            whiteSpace: 'nowrap', // Prevent text wrapping in all cells
            overflow: 'visible'
          }
        }}
      >
        {props.children}
      </MUITableRow>
      {
        drawerOpened ?
          <MUITableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={props.drawerColumnSpan}>
              <Collapse in={drawerOpened} timeout={500} unmountOnExit>
                <Box sx={{ mx: 0, my: 2 }} minHeight={200}>
                  <Stack direction='row'
                    sx={{
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      width: '100%'
                    }}
                    spacing={3}>
                    <Stack direction="column" spacing={2} sx={{ width: '15%' }}>
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => {
                          updateDrawerState(!drawerOpened);

                          if (props.drawerOpened)
                            props.drawerOpened(!drawerOpened, props.data);
                        }}
                      >
                        {drawerOpened ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>

                      {props.drawerButtonBar}
                    </Stack>
                    {props.drawerChildren}
                  </Stack>
                </Box>
              </Collapse>
            </TableCell>
          </MUITableRow> : <></>
      }
    </>
  );
}

export default TableRow;